import React from 'react';
import {Field, reduxForm} from 'redux-form';
import {Button, Header} from 'semantic-ui-react';
import {compose} from 'redux';
import PropTypes from 'prop-types';
import {
    get as _get,
    isNil as _isNil,
    isEmpty as _isEmpty,
} from 'lodash';
import moment from 'moment';

import {graphql} from 'react-apollo';
import * as ContentGraphQL from '@graphql/clients/mutation';
import {clipConfigOptions} from '@graphql/clipConfiguration/query';
import {CLIENT_PACKAGE_CONTENT} from '@constants/resources';
import Form from '@appComponents/ReduxFormControls';
import {FormWrapper} from '@appComponents/HOCFormWrapper';
import {ButtonCancel} from '@appComponents/ButtonCollection';
import {IconInfoCircle} from '@appComponents/IconCollection';
import MessageBox from '@appComponents/MessageBox';
import {
    convertToInt,
    defaultValuePrice,
    parseFloatOrNull,
    parseIntOrNull,
    parseToBoolean,
    scrollToTop,
} from '@utils/helpers';
import {hideModal, showModalConfirmation} from '@utils/modal';
import {showMessageBox} from '@utils/messageBox';
import {showPopupMessage} from '@utils/popupMessage/PopupMessageService';
import EventContentTypesField from '@modules/client/forms/ClientPackageContent/Fields/EventContentTypesField';
import EventContentVariantsField from '@modules/client/forms/ClientPackageContent/Fields/EventContentVariantsField';
import {YES_OR_NO_FOR_PACKAGE_FORM} from '@constants/staticDropdowns';
import {getLastMinuteOfToday, getOneHourAgoDateTime, getTodayDate} from '@utils/date';
import {areFormDataEqual, parseEventContentTypes} from '@modules/client/utils/contentPackageFormHelper';

import validate from './validatorClientContentPackage';

class ContentPackageForm extends React.Component {
    static propTypes = {
        bookingTypes: PropTypes.array.isRequired,
        eventContentVariants: PropTypes.array.isRequired,
        clientPackageId: PropTypes.number.isRequired,
        CreateClientPackageContent: PropTypes.func,
        ClipConfigOptions: PropTypes.object,
        dataForForm: PropTypes.object,
        dataMap: PropTypes.object,
        entityName: PropTypes.string.isRequired,
        formData: PropTypes.object,
        handleSubmit: PropTypes.func,
        parentTreeData: PropTypes.object.isRequired,
        reloadTable: PropTypes.func.isRequired,
        submitting: PropTypes.bool,
        treeData: PropTypes.array,
        UpdateClientPackageContent: PropTypes.func,
        UpdateBookingsPrice: PropTypes.func,
        header: PropTypes.string,
        isActive: PropTypes.bool,
        treeActiveStatus: PropTypes.object,
        productVariants: PropTypes.array,
        product: PropTypes.object,
        level: PropTypes.number,
        backToIndexUrl: PropTypes.func,
        DeleteClientPackageContent: PropTypes.func,
    };

    static defaultProps = {
        isActive: false,
    };

    state = {
        updatePastBookings: false,
        displayUpdatePastBookingsSwitch: false,
        shouldShowDatePicker: false,
        maxTime: '',
        startDatetime: '',
    };

    updatePriceOfPastBookings = async (dataToSave) => {
        const dataToUpdatePastBookingsPrice = {
            id: dataToSave.id,
            start_datetime: dataToSave.start_datetime,
        };

        if (areFormDataEqual(dataToSave, this.props.formData) && dataToSave.update_past_bookings) {
            this.props.UpdateBookingsPrice({
                variables: {...dataToUpdatePastBookingsPrice},
            });
        } else {
            try {
                await this.props.UpdateClientPackageContent({
                    variables: {...dataToSave},
                });
            } catch {
                throw new Error('Error while updating client package content.');
            }

            if (dataToSave.update_past_bookings) {
                try {
                    await this.props.UpdateBookingsPrice({
                        variables: {...dataToUpdatePastBookingsPrice},
                    });
                } catch {
                    throw new Error('The price for event past bookings was not updated.');
                }
            }
        }
    };

    formSubmit = async (data) => {
        const eventPrice = parseFloatOrNull(data.event_price);
        const numBookableEvents = parseIntOrNull(data.num_bookable_events);
        const shouldUpdatePastBookings = eventPrice || 0 === eventPrice ? data.update_past_bookings : false;
        const startDatetime = shouldUpdatePastBookings ? data.start_datetime : null;

        if (
            !data.event_content_variants.length
            &&!data.event_content_type_configs?.length
            && null === numBookableEvents
            && !data.booking_type
            && !data.event_booking_type
            && _isNil(data.is_hq)
            && _isNil(data.is_ll)
            && null === eventPrice
            && !startDatetime
            && _isNil(data.sla_minimum_threshold)
        ) {
            showMessageBox('contentPackageFormErrorBox', null, 'At least one field should not be empty.', 'error');

            return;
        }

        let dataToSave = Object.assign({}, this.props.dataMap, data),
            dataSaved;

        const entityName = `settings for "${this.props.entityName}"`;

        dataToSave.num_bookable_events = numBookableEvents;
        dataToSave.booking_type = dataToSave.booking_type || null;
        dataToSave.event_booking_type = dataToSave.event_booking_type || null;
        dataToSave.is_hq = _isNil(dataToSave.is_hq) ? null : parseToBoolean(convertToInt(dataToSave.is_hq));
        dataToSave.is_ll = _isNil(dataToSave.is_ll) ? null : parseToBoolean(convertToInt(dataToSave.is_ll));
        dataToSave.event_price = eventPrice;
        dataToSave.event_content_type_configs = parseEventContentTypes(dataToSave.event_content_type_configs, dataToSave.id);
        dataToSave.start_datetime = startDatetime + ':00';
        dataToSave.sla_minimum_threshold =
            _isNil(dataToSave.sla_minimum_threshold) ? null : convertToInt(dataToSave.sla_minimum_threshold);

        if (!dataToSave.id) {
            const defaultData = {
                sport: null,
                tournament: null,
                tournament_category: null,
                property_licence: null,
                stage_category: null,
            };

            dataToSave = {...defaultData, ...this.props.parentTreeData, ...dataToSave, ...{
                client_package: convertToInt(this.props.clientPackageId),
            }};

            dataSaved = await this.props.CreateClientPackageContent({
                variables: {...dataToSave},
            });

            dataToSave.id = _get(dataSaved.data, 'createClientPackageContent.id', null);

            if (shouldUpdatePastBookings && dataToSave.id) {
                const updateData = {...dataToSave, id: dataToSave.id};
                dataSaved = this.props.UpdateBookingsPrice({
                    variables: {...updateData},
                });
            }
        } else {
            if (!shouldUpdatePastBookings) {
                dataSaved = this.props.UpdateClientPackageContent({
                    variables: {...dataToSave},
                });
            } else {
                dataSaved = this.updatePriceOfPastBookings(dataToSave);
            }
        }

        try {
            await dataSaved;
            showPopupMessage(`The ${entityName} have been saved successfully.`, 'success');
            this.props.reloadTable();
        } catch (error) {
            showPopupMessage(
                `The ${entityName} have not been saved successfully.`,
                'error',
                true,
                `${error}`
            );
        } finally {
            hideModal();
        }
    };

    isAnyParentActive = (treeActiveStatus) => {
        if (!treeActiveStatus.parent) {
            return false;
        }

        if (treeActiveStatus.is_active) {
            return true;
        }

        return this.isAnyParentActive(treeActiveStatus.parent);
    };

    deleteContentPackage = () => {
        const entityName = ` settings for "${this.props.entityName}"`,
            contentPackageId = this.props.dataForForm.id,
            isActive = this.props.dataForForm.is_active,
            parentIsActive = this.props.treeActiveStatus.parent?.is_active;

        let handleDeletePackage;

        showModalConfirmation({
            header: <Header icon='trash' content={'Remove content settings'}/>,
            text: `The settings for "${this.props.entityName}" are being removed...`,
        });

        handleDeletePackage = (this.props.formData.territory_selection_type || isActive !== parentIsActive)
            ? this.props.UpdateClientPackageContent({
                variables: {
                    id: contentPackageId,
                    booking_type: null,
                    event_booking_type: null,
                    event_content_type_configs: [],
                    event_content_variants: null,
                    is_hq: null,
                    is_ll: null,
                    num_bookable_events: null,
                    event_price: null,
                    sla_minimum_threshold: null,
                },
            })
            : this.props.DeleteClientPackageContent({
                variables: {id: contentPackageId},
            });

        handleDeletePackage.then(() => {
            showPopupMessage(`The ${entityName} have been removed successfully.`, 'success');
            hideModal();
            this.props.reloadTable();

            return false;
        }).catch((error) => {
            hideModal();
            scrollToTop();
            showPopupMessage(this.props.entityName, 'error', true, error?.message);
        });
    };

    getIdsForChildren = (data, id, getChildren = false) => {
        let ids = [];

        if (!data) {
            return ids;
        }

        for (let i = 0; i < data.length; i++) {
            const rowData = data[i];

            if (rowData.id && getChildren) {
                ids.push(parseInt(rowData.id));
            }

            if (rowData.children && 0 < rowData.children.length) {
                ids = ids.concat(this.getIdsForChildren(rowData.children, id, (getChildren || rowData.id === id)));
            }
        }

        return ids;
    };

    deleteContentPackageAction = () => {
        showModalConfirmation({
            header: <Header icon='trash' content='Remove content settings'/>,
            onYes: this.deleteContentPackage,
            onNo: () => this.props.backToIndexUrl(),
            text: `Are you sure you want to remove the settings for "${this.props.entityName}"?`,
        });
    };

    renderDeleteButton = () => {
        let deleteButton = null;

        if (this.props.dataForForm.id
            && (this.props.dataForForm.booking_type
                || this.props.dataForForm.event_booking_type
                || this.props.dataForForm.event_content_variants.length
                || this.props.dataForForm.num_bookable_events
                || this.props.dataForForm.event_content_type_configs.length
                || !_isEmpty(this.props.dataForForm.event_price)
                || !_isNil(this.props.dataForForm.is_hq)
                || !_isNil(this.props.dataForForm.is_ll)
                || !_isNil(this.props.dataForForm.sla_minimum_threshold))
        ) {
            deleteButton =
                <Button onClick={this.deleteContentPackageAction}
                    color='red'
                    disabled={this.props.submitting}
                    icon='trash'
                    content='Remove settings'/>;
        }

        return deleteButton;
    };

    showDatePicker = (toggleShow) => {
        this.setState({shouldShowDatePicker: toggleShow});
    };

    showUpdatePastBookings = (event) => {
        const currentEventPrice = this.props.formData.event_price;
        const newEventPrice = _get(event, 'target.value', null);

        if ((currentEventPrice || 0 === currentEventPrice) && '' !== newEventPrice || newEventPrice) {
            this.setState({displayUpdatePastBookingsSwitch: true});
        } else {
            this.setState({
                displayUpdatePastBookingsSwitch: false,
                shouldShowDatePicker: false,
            });
        }
    };

    updateMaxTimeBasedOnDate(selectedDateTime, options) {
        let maxTime;

        if (moment(selectedDateTime).isSameOrAfter(getTodayDate(), 'hour')) {
            maxTime = options.first;
        } else {
            maxTime = options.second;
        }

        this.setState({maxTime});
    }

    onCancel = () => {
        hideModal();
        this.props.backToIndexUrl();
    };

    render() {
        return (
            <div>
                <Header as='h3'>{this.props.header}</Header>
                <MessageBox name={'contentPackageFormErrorBox'} className='--formInnerError'/>
                <Form.Create
                    onSubmit={this.props.handleSubmit(this.formSubmit)}
                    loading={(this.props.formData !== undefined) ? this.props.formData.loading : false}
                    className={'client-content-package-form --paddingTop-0'}
                >
                    <Form.IdField defaultValue={ this.props.formData.id } />
                    <Form.IdField hidden={true} name={'is_active'} defaultValue={ this.props.isActive } />
                    {this.props.product.has_event_booking_support &&
                        <div className='formRow --align-baseline'>
                            <div className='--label-container'><label>Event booking type</label></div>
                            <div className='input-container'>
                                <Field
                                    component={Form.SemanticSelect}
                                    name='event_booking_type'
                                    placeholder='Same as parent'
                                    allowClear={true}
                                    defaultValue={_get(this.props, 'formData.event_booking_type.id', null)}
                                    options={this.props.bookingTypes}
                                    required
                                />
                            </div>
                        </div>
                    }
                    <div className='formRow --align-baseline'>
                        <div className='--label-container'><label>Booking type</label></div>
                        <div className='input-container'>
                            <Field
                                component={Form.SemanticSelect}
                                name='booking_type'
                                placeholder='Same as parent'
                                allowClear={true}
                                defaultValue={_get(this.props, 'formData.booking_type.id', null)}
                                options={this.props.bookingTypes}
                                required
                            />
                        </div>
                    </div>
                    <div className='formRow --align-center'>
                        <div className='--label-container'><label>Content variants</label></div>
                        <div className='input-container checkboxes-inline'>
                            <EventContentVariantsField
                                productVariants={this.props.productVariants}
                                eventContentVariants={this.props.eventContentVariants}
                                selectedContentVariants={_get(this.props, 'formData.event_content_variants', null)}
                            />
                            &nbsp;<IconInfoCircle className={'--event-price-info'} tooltip={'Leave empty to inherit from parent.'} />
                        </div>
                    </div>
                    {this.props.product.has_hq_support &&
                        <div className='formRow --align-baseline'>
                            <div className='--label-container'><label>HQ</label></div>
                            <div className='input-container'>
                                <Field
                                    component={Form.SemanticSelect}
                                    name='is_hq'
                                    placeholder='Same as parent'
                                    allowClear={true}
                                    defaultValue={_isNil(this.props.formData.is_hq) ? null : this.props.formData.is_hq ? '1' : '0'}
                                    options={YES_OR_NO_FOR_PACKAGE_FORM}
                                />
                            </div>
                        </div>
                    }
                    {this.props.product.has_ll_support &&
                        <div className='formRow --align-baseline'>
                            <div className='--label-container'><label>LL</label></div>
                            <div className='input-container'>
                                <Field
                                    component={Form.SemanticSelect}
                                    name='is_ll'
                                    placeholder='Same as parent'
                                    allowClear={true}
                                    defaultValue={_isNil(this.props.formData.is_ll) ? null : this.props.formData.is_ll ? '1' : '0'}
                                    options={YES_OR_NO_FOR_PACKAGE_FORM}
                                />
                            </div>
                        </div>
                    }
                    <div className='formRow --align-center'>
                        <div className='--label-container'><label>Number of bookable events</label></div>
                        <div className='input-container'>
                            <Field
                                component={Form.SemanticInput}
                                defaultValue={this.props.formData.num_bookable_events}
                                name='num_bookable_events'
                                min={0}
                                type='number'
                            />
                        </div>
                    </div>
                    <div className='formRow --align-baseline'>
                        <div className='--label-container'><label>Event price</label></div>
                        <div className='input-container'>
                            <Field
                                component={Form.SemanticInput}
                                defaultValue={defaultValuePrice(this.props.formData.event_price)}
                                name='event_price'
                                min={0}
                                onChange={this.showUpdatePastBookings}
                                icon='euro'
                                type='number'
                                className='client-content-package-form--event-price'
                            />
                            &nbsp;<IconInfoCircle className={'--event-price-info'} tooltip={'Leave empty to inherit from parent.'} />
                        </div>
                    </div>
                    {this.state.displayUpdatePastBookingsSwitch &&
                        <div className='formRow --align-center'>
                            <div className='--label-container'><label>Update price of past bookings?</label></div>
                            <div className='input-container'>
                                <Field
                                    component={Form.SemanticInput}
                                    type='toggle'
                                    name='update_past_bookings'
                                    onChange={this.showDatePicker}
                                    className='client-content-package-form--update-price'
                                    defaultValue={this.state.updatePastBookings}
                                />
                            </div>
                        </div>
                    }
                    {this.state.shouldShowDatePicker &&
                        <div className='formRow --align-baseline'>
                            <div className='--label-container'><label>Update from*</label></div>
                            <div className='input-container --start_datetime'>
                                <Field
                                    className='--minimalWidth --datetimeWithoutSeconds'
                                    component={Form.SemanticInput}
                                    name='start_datetime'
                                    type='date'
                                    onChange={(startDatetime) => {
                                        this.updateMaxTimeBasedOnDate(startDatetime, {
                                            first: getOneHourAgoDateTime(),
                                            second: getLastMinuteOfToday(),
                                        });
                                    }}
                                    configuration={{
                                        minTime: moment().hours(0).minutes(0),
                                        maxTime: this.state.maxTime,
                                        minDate: moment().subtract(1, 'years'),
                                        maxDate: getLastMinuteOfToday(),
                                        timeFormat: 'HH:mm',
                                        showTimeSelect: true,
                                        shouldCloseOnSelect: false,
                                    }}
                                    defaultValue={getTodayDate()}
                                />
                                &nbsp;<IconInfoCircle tooltip={'Please select a date and time in the past but not earlier than 1 year ago.'} />
                            </div>
                        </div>
                    }
                    {this.props.product.has_event_booking_support &&
                        <div className='formRow'>
                            <div className='--label-container --align-baseline'><label>Clips</label></div>
                            <div className='input-container'>
                                <EventContentTypesField
                                    ClipConfigOptions={this.props.ClipConfigOptions}
                                    selectedOptions={_get(this.props, 'formData.event_content_type_configs', null)}
                                    level={this.props.level}
                                />
                            </div>
                        </div>
                    }
                    {this.props.product.is_invoiceable && <div className='formRow --align-baseline'>
                        <div className='--label-container'><label>SLA minimum threshold</label></div>
                        <div className='input-container'>
                            <Field
                                component={Form.SemanticInput}
                                defaultValue={_get(this.props, 'formData.sla_minimum_threshold', null)}
                                name='sla_minimum_threshold'
                                min={0}
                                max={100}
                                icon='percent'
                                type='number'
                                onlyDigitsAllowed={true}
                            />
                            &nbsp;<IconInfoCircle
                                tooltip={<>Defines the minimum threshold (percentage of stream coverage) for a stream to be invoiceable.
                                    <br />Leave empty to inherit from parent.</>}/>
                        </div>
                    </div>
                    }
                    {this.props.formData.id && <Form.FormRowChangelog
                        resources={CLIENT_PACKAGE_CONTENT}
                        data={ (this.props.formData || {}) }
                    />}
                    <div className='formRow form__footer'>
                        <label/>
                        <Button
                            color='blue'
                            type='submit'
                            loading={ this.props.submitting }
                            disabled={ this.props.submitting }
                            icon='save'
                            content='Save'/>
                        { this.renderDeleteButton() }
                        <ButtonCancel
                            disabled={this.props.submitting}
                            onClick={ this.onCancel }
                        >
                            Cancel
                        </ButtonCancel>
                    </div>
                </Form.Create>
            </div>
        );
    }
}

const ContentPackageFormWithRedux = reduxForm({form: 'ContentPackageForm', validate})(ContentPackageForm);

export default (compose(
    graphql(ContentGraphQL.updateBookingsPrice, {name: 'UpdateBookingsPrice'}),
    graphql(ContentGraphQL.updateClientPackageContent, {name: 'UpdateClientPackageContent'}),
    graphql(ContentGraphQL.createClientPackageContent, {name: 'CreateClientPackageContent'}),
    graphql(ContentGraphQL.deleteClientPackageContent, {name: 'DeleteClientPackageContent'}),
    graphql(clipConfigOptions, {
        skip: (props) => {
            return !props.product.has_event_booking_support;
        },
        options: (props) => ({
            notifyOnNetworkStatusChange: false,
            fetchPolicy: 'no-cache',
            variables: {
                preferredClipConfigFilters: {
                    product: [props.product.id],
                    tournaments: [props.formData.tournament?.id],
                },
                fallbackClipConfigsFilters: {
                    product: [props.product.id],
                    sports: [props.formData.sport?.id ?? props.parentTreeData?.sportId],
                    tournamentsEmpty: true,
                },
            },
        }),
        name: 'ClipConfigOptions',
    })
)(FormWrapper({
    id: '',
    is_active: false,
    booking_type: null,
    event_booking_type: null,
    event_content_variants: null,
    is_hq: null,
    is_ll: null,
    event_content_type_configs: [],
    num_bookable_events: null,
    event_price: null,
    update_past_bookings: false,
    level: null,
    sla_minimum_threshold: null,
}, ContentPackageFormWithRedux)));
